// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var GLOBAL_TENANT_COMPOSITE = "main-main";

// src/tenancy/index.ts
var TenantContext = class _TenantContext {
  customerId;
  orgId;
  tenantId;
  userId;
  constructor(init = {}) {
    this.customerId = init.customerId ?? null;
    this.orgId = init.orgId || GLOBAL_TENANT;
    this.tenantId = init.tenantId || GLOBAL_TENANT;
    this.userId = init.userId ?? null;
  }
  /** Composite key `{org_id}-{tenant_id}` used in DDB SKs + global-tier checks. */
  get orgIdTenantId() {
    return `${this.orgId}-${this.tenantId}`;
  }
  get isGlobal() {
    return this.orgIdTenantId === GLOBAL_TENANT_COMPOSITE;
  }
  get isUserScoped() {
    return this.userId != null && this.userId.length > 0;
  }
  get hasCustomerScope() {
    return this.customerId != null && this.customerId.length > 0 || this.orgId !== GLOBAL_TENANT;
  }
  withUser(userId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId
    });
  }
  withOrg(orgId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  withTenant(tenantId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId,
      userId: this.userId
    });
  }
  withCustomer(customerId) {
    return new _TenantContext({
      customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  toJSON() {
    return {
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId,
      orgIdTenantId: this.orgIdTenantId
    };
  }
};

// src/teams/team.ts
function teamTenantKey(team) {
  return new TenantContext(team.tenant).orgIdTenantId;
}
function teamKey(team) {
  return `${teamTenantKey(team)}:${team.id}`;
}
function sameTeam(a, b) {
  return teamKey(a) === teamKey(b);
}
function initials(source, max = 2) {
  const parts = (source ?? "").trim().split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, max).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// src/teams/member.ts
var MemberStatus = {
  ACTIVE: "active",
  /** Invited but hasn't accepted yet (mirrors a pending invite). */
  INVITED: "invited",
  /** Access revoked but record retained. */
  SUSPENDED: "suspended"
};
var MEMBER_STATUS_LABEL = {
  active: "Active",
  invited: "Invited",
  suspended: "Suspended"
};
function memberInitials(member) {
  return initials(member.name?.trim() || member.email?.split("@")[0] || "");
}

// src/auth/action-vocabulary.ts
var MANAGE_ACTION = "*";
var DEFAULT_SCOPE_ACTIONS = ["list", "show", "create", "edit", "delete"];
var ACTION_TO_SCOPE_ACTIONS = {
  read: ["list", "show"],
  list: ["list"],
  show: ["show"],
  create: ["create"],
  update: ["edit"],
  edit: ["edit"],
  delete: ["delete"],
  export: ["export"],
  invoke: ["invoke"],
  [MANAGE_ACTION]: DEFAULT_SCOPE_ACTIONS
};
function scopeActionsFor(action) {
  const key = action.trim().toLowerCase();
  return ACTION_TO_SCOPE_ACTIONS[key] ?? [key];
}
function actionsGrant(actions, wanted) {
  if (!actions || actions.length === 0) return false;
  if (actions.includes(MANAGE_ACTION)) return true;
  const target = new Set(scopeActionsFor(wanted));
  for (const held of actions) {
    for (const granted of scopeActionsFor(held)) {
      if (target.has(granted)) return true;
    }
  }
  return false;
}
var TEAM_GROUP_SCOPES = {
  team: {
    read: "teams:read",
    list: "teams:list",
    show: "teams:read",
    create: "teams:create",
    update: "teams:update",
    edit: "teams:update",
    delete: "teams:delete"
  },
  team_members: {
    read: "team-members:read",
    list: "team-members:list",
    show: "team-members:read",
    create: "team-members:create",
    update: "team-members:update",
    edit: "team-members:update",
    delete: "team-members:delete"
  },
  team_roles: {
    read: "team-roles:read",
    list: "team-roles:list",
    show: "team-roles:read",
    create: "team-roles:create",
    update: "team-roles:update",
    edit: "team-roles:update",
    delete: "team-roles:delete"
  },
  team_invites: {
    read: "team-invites:read",
    list: "team-invites:list",
    show: "team-invites:read",
    // An invite is sent and revoked, not created and deleted.
    create: "team-invites:send",
    update: "team-invites:resend",
    edit: "team-invites:resend",
    delete: "team-invites:revoke"
  },
  // Read-only by nature: there is no write verb to map.
  team_audit: {
    read: "team-audit:read",
    list: "team-audit:read",
    show: "team-audit:read"
  }
};
function teamScopeFor(group, action) {
  return TEAM_GROUP_SCOPES[group.trim()]?.[action.trim().toLowerCase()];
}
function splitScope(scope) {
  const i = scope.lastIndexOf(":");
  if (i <= 0 || i === scope.length - 1) return null;
  return [scope.slice(0, i), scope.slice(i + 1)];
}
function rolePermissionsGrant(rolePermissions, resource, action) {
  if (!rolePermissions) return false;
  if (actionsGrant(rolePermissions[resource], action)) return true;
  const scope = teamScopeFor(resource, action);
  if (!scope) return false;
  const parsed = splitScope(scope);
  if (!parsed) return false;
  const held = rolePermissions[parsed[0]];
  if (!held || held.length === 0) return false;
  return held.includes(MANAGE_ACTION) || held.includes(parsed[1]);
}

// src/auth/casbin.ts
function enforceFromRolePermissions(rolePermissions, resource, action) {
  return rolePermissionsGrant(rolePermissions, resource, action);
}
function permissionSetFromRolePermissions(rolePermissions) {
  return { can: (resource, action) => enforceFromRolePermissions(rolePermissions, resource, action) };
}

// src/auth/resource-groups.ts
var TEAM = "team";
var TEAM_MEMBERS = "team_members";
var TEAM_ROLES = "team_roles";
var TEAM_INVITES = "team_invites";
var TEAM_AUDIT = "team_audit";
var TEAM_MANAGEMENT_RESOURCES = [
  TEAM,
  TEAM_MEMBERS,
  TEAM_ROLES,
  TEAM_INVITES,
  TEAM_AUDIT
];
var COLLABORATIVE_RESOURCES = [
  "transactions",
  "addresses",
  "entities",
  "tags",
  "labels",
  "groups",
  "disputes",
  "streams",
  "pipelines",
  "workspaces",
  "notifications"
];
var RESOURCE_GROUP_LABEL = {
  transactions: "Transactions",
  addresses: "Addresses",
  entities: "Entities",
  tags: "Tags",
  labels: "Labels",
  groups: "Groups",
  disputes: "Disputes",
  streams: "Streams",
  pipelines: "Pipelines",
  workspaces: "Workspaces",
  notifications: "Notifications",
  [TEAM]: "Team settings",
  [TEAM_MEMBERS]: "Team members",
  [TEAM_ROLES]: "Roles",
  [TEAM_INVITES]: "Invites",
  [TEAM_AUDIT]: "Audit log"
};
var TEAM_GROUP_DESCRIPTION = {
  [TEAM]: "Rename the team and change its settings.",
  [TEAM_MEMBERS]: "Invite, edit, and remove members.",
  [TEAM_ROLES]: "Create and edit roles + permissions.",
  [TEAM_INVITES]: "Send, resend and revoke invitations.",
  [TEAM_AUDIT]: "View the team activity log."
};
[
  ...COLLABORATIVE_RESOURCES.map((id) => ({ id, label: RESOURCE_GROUP_LABEL[id] ?? id })),
  ...TEAM_MANAGEMENT_RESOURCES.map((id) => ({
    id,
    label: RESOURCE_GROUP_LABEL[id] ?? id,
    description: TEAM_GROUP_DESCRIPTION[id]
  }))
];
function mergeMatrices(...matrices) {
  const out = {};
  for (const matrix of matrices) {
    for (const [group, actions] of Object.entries(matrix)) {
      const merged = /* @__PURE__ */ new Set([...out[group] ?? [], ...actions]);
      out[group] = merged.has(MANAGE_ACTION) ? [MANAGE_ACTION] : [...merged];
    }
  }
  return out;
}

// src/teams/role.ts
var TeamResource = {
  MEMBERS: "team_members",
  ROLES: "team_roles",
  INVITES: "team_invites",
  AUDIT: "team_audit",
  TEAM: "team"
};
function rolesForMember(member, roles) {
  const byId = new Map(roles.map((role) => [role.id, role]));
  return member.roleIds.map((id) => byId.get(id)).filter((role) => Boolean(role));
}
function effectiveMatrix(member, roles) {
  return mergeMatrices(...rolesForMember(member, roles).map((role) => role.permissions));
}
function memberPermissionSet(member, roles) {
  return permissionSetFromRolePermissions(effectiveMatrix(member, roles));
}

// src/teams/system-roles.ts
var TeamRoleKey = {
  OWNER: "owner",
  ADMIN: "admin",
  MEMBER: "member",
  VIEWER: "viewer"
};
var READ = ["read"];
var CONTRIBUTOR = ["read", "create", "update"];
var CRUD = ["read", "create", "update", "delete"];
function collaborativeMatrix(actions) {
  const out = {};
  for (const group of COLLABORATIVE_RESOURCES) out[group] = [...actions];
  return out;
}
function systemRoleSpecs() {
  return [
    {
      key: TeamRoleKey.OWNER,
      role: {
        id: "owner",
        name: "Owner",
        description: "Full control of the team, members, roles, invites and all team objects.",
        system: true,
        permissions: {
          [TEAM]: [...CRUD],
          [TEAM_MEMBERS]: [...CRUD],
          [TEAM_ROLES]: [...CRUD],
          [TEAM_INVITES]: [...CRUD],
          [TEAM_AUDIT]: [...READ],
          ...collaborativeMatrix(CRUD)
        }
      }
    },
    {
      key: TeamRoleKey.ADMIN,
      role: {
        id: "admin",
        name: "Admin",
        description: "Manage members, roles, invites and all team objects; edit team settings.",
        system: true,
        permissions: {
          [TEAM]: ["read", "update"],
          [TEAM_MEMBERS]: [...CRUD],
          [TEAM_ROLES]: [...CRUD],
          [TEAM_INVITES]: [...CRUD],
          [TEAM_AUDIT]: [...READ],
          ...collaborativeMatrix(CRUD)
        }
      }
    },
    {
      key: TeamRoleKey.MEMBER,
      role: {
        id: "member",
        name: "Member",
        description: "Standard team member: read the team + contribute to team objects.",
        system: true,
        permissions: {
          [TEAM]: [...READ],
          [TEAM_MEMBERS]: [...READ],
          ...collaborativeMatrix(CONTRIBUTOR)
        }
      }
    },
    {
      key: TeamRoleKey.VIEWER,
      role: {
        id: "viewer",
        name: "Viewer",
        description: "Read-only access to the team and its objects.",
        system: true,
        permissions: {
          [TEAM]: [...READ],
          ...collaborativeMatrix(READ)
        }
      }
    }
  ];
}
var SPEC_ROLE_ALIASES = {
  org_admin: TeamRoleKey.OWNER,
  tenant_admin: TeamRoleKey.ADMIN,
  writer: TeamRoleKey.MEMBER,
  reader: TeamRoleKey.VIEWER
};
var CANONICAL_KEYS = new Set(Object.values(TeamRoleKey));
function resolveRoleKey(name) {
  const normalized = name.trim().toLowerCase();
  if (CANONICAL_KEYS.has(normalized)) return normalized;
  return SPEC_ROLE_ALIASES[normalized];
}
function systemRoleFor(name) {
  const key = resolveRoleKey(name);
  if (!key) return void 0;
  return systemRoleSpecs().find((spec) => spec.key === key)?.role;
}

// src/teams/invite.ts
var InviteStatus = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  REVOKED: "revoked",
  EXPIRED: "expired"
};
var INVITE_STATUS_LABEL = {
  pending: "Pending",
  accepted: "Accepted",
  revoked: "Revoked",
  expired: "Expired"
};
var INVITE_EMAIL_NOT_SENT = "The invitation is saved, but its email couldn't be sent. Copy its acceptance link, or renew it from the Invites tab.";
var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_RE.test(email.trim());
}
function isInviteActionable(invite) {
  return invite.status === InviteStatus.PENDING || invite.status === InviteStatus.EXPIRED;
}

// src/events/index.ts
var EventCriticality = {
  CRITICAL: 1,
  HIGH: 2,
  MEDIUM: 3,
  LOW: 4,
  SUCCESS: 5
};
var EventResourceType = {
  ACTIVITY_LOG: "activity_log"
};

// src/teams/audit.ts
var SEVERITY_TO_CRITICALITY = {
  critical: EventCriticality.CRITICAL,
  high: EventCriticality.HIGH,
  medium: EventCriticality.MEDIUM,
  low: EventCriticality.LOW,
  success: EventCriticality.SUCCESS
};
function inferSeverity(action) {
  if (/remove|delete|revoke|suspend/.test(action)) return "high";
  if (/create|invite|add|accept|grant/.test(action)) return "success";
  return "low";
}
function toActivityLogEvents(entries) {
  return entries.map((entry) => ({
    id: entry.id,
    created_at: entry.at,
    resource: EventResourceType.ACTIVITY_LOG,
    criticality_level: SEVERITY_TO_CRITICALITY[entry.severity ?? inferSeverity(entry.action)],
    message_summary: entry.summary,
    function_name: entry.action,
    payload_summary: entry.target,
    created_by: entry.actor ?? null,
    metadata: entry.metadata
  }));
}

export { COLLABORATIVE_RESOURCES, CONTRIBUTOR, CRUD, INVITE_EMAIL_NOT_SENT, INVITE_STATUS_LABEL, InviteStatus, MEMBER_STATUS_LABEL, MemberStatus, READ, SPEC_ROLE_ALIASES, TEAM, TEAM_AUDIT, TEAM_INVITES, TEAM_MANAGEMENT_RESOURCES, TEAM_MEMBERS, TEAM_ROLES, TeamResource, TeamRoleKey, effectiveMatrix, isInviteActionable, isValidEmail, memberInitials, memberPermissionSet, resolveRoleKey, rolesForMember, sameTeam, systemRoleFor, systemRoleSpecs, teamKey, teamTenantKey, toActivityLogEvents };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map